import React, { useState, useEffect, useCallback } from 'react';
import {
  getSettings, saveSettings,
  listFacilities, createFacility, archiveFacility,
  listPatients,
} from '../lib/assessment-kiosk-admin-api';
import { FacilityResidents } from '../components/assessment-kiosk/FacilityResidents.jsx';
import { FacilityCaregivers } from '../components/assessment-kiosk/FacilityCaregivers.jsx';
import { FacilityDevices } from '../components/assessment-kiosk/FacilityDevices.jsx';
import { FacilityElationTags } from '../components/assessment-kiosk/FacilityElationTags.jsx';

export function AssessmentKioskAdminPage({ user }) {
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  // ── Settings state ───────────────────────────────────────────────────────
  const [settings, setSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  // ── Facilities state ─────────────────────────────────────────────────────
  const [facilities, setFacilities] = useState([]);
  const [activeFacility, setActiveFacility] = useState(null);
  const [patients, setPatients] = useState([]);
  const [newFacilityName, setNewFacilityName] = useState('');
  const [creatingFacility, setCreatingFacility] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, f, p] = await Promise.all([
        getSettings().catch(() => ({})),
        listFacilities().catch(() => []),
        listPatients().catch(() => []),
      ]);
      setSettings(s && typeof s === 'object' ? s : {});
      const flist = f.facilities || f || [];
      setFacilities(flist);
      setPatients(Array.isArray(p) ? p : (p.patients || []));
      // Keep active facility in sync if list refreshes (functional update avoids dep on activeFacility)
      setActiveFacility((prev) => (prev ? (flist.find((x) => x.id === prev.id) || null) : null));
    } catch (e) { setFacilitiesError(e.message); }
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  // ── Settings helpers ─────────────────────────────────────────────────────
  const enabled        = settings.bhi_assessment_kiosk_enabled === true;
  const scaleMode      = settings.bhi_assessment_kiosk_scale_mode ?? 'top';
  const pinMaxAttempts = settings.bhi_assessment_kiosk_pin_max_attempts ?? 5;
  const lockoutMinutes = settings.bhi_assessment_kiosk_pin_lockout_minutes ?? 15;
  const item9Message   = settings.bhi_assessment_kiosk_item9_message ?? '';

  async function saveSetting(key, value) {
    if (!isAdmin) { setSettingsError('Only an admin can change practice settings.'); return; }
    const prev = settings;
    setSettings({ ...prev, [key]: value });
    setSavingSettings(true); setSettingsError(null);
    try { await saveSettings({ [key]: value }); }
    catch (e) { setSettings(prev); setSettingsError(e.message || 'Could not save setting.'); }
    finally { setSavingSettings(false); }
  }

  async function saveItem9Message() {
    await saveSetting('bhi_assessment_kiosk_item9_message', item9Message);
  }

  // ── Facility helpers ─────────────────────────────────────────────────────
  // A saved tag list comes back as the facility row; keep the list and the open
  // panel on the same row so re-opening the facility shows what was saved.
  function onFacilitySaved(row) {
    setFacilities((list) => list.map((f) => (f.id === row.id ? { ...f, ...row } : f)));
    setActiveFacility((prev) => (prev && prev.id === row.id ? { ...prev, ...row } : prev));
  }

  async function onCreateFacility() {
    if (!newFacilityName.trim()) { setFacilitiesError('Enter a facility name.'); return; }
    setCreatingFacility(true); setFacilitiesError(null);
    try {
      const res = await createFacility(newFacilityName.trim());
      setNewFacilityName('');
      const refreshed = await listFacilities().catch(() => []);
      const flist = refreshed.facilities || refreshed || [];
      setFacilities(flist);
      // Auto-select newly created facility
      const created = flist.find((f) => f.id === res.id) || flist[flist.length - 1];
      if (created) setActiveFacility(created);
    } catch (e) { setFacilitiesError(e.message); }
    finally { setCreatingFacility(false); }
  }

  async function onArchiveFacility(f) {
    if (!window.confirm(`Archive facility "${f.name}"? This is reversible only by a database admin.`)) return;
    setFacilitiesError(null);
    try {
      await archiveFacility(f.id);
      if (activeFacility?.id === f.id) setActiveFacility(null);
      const refreshed = await listFacilities().catch(() => []);
      setFacilities(refreshed.facilities || refreshed || []);
    } catch (e) { setFacilitiesError(e.message); }
  }

  if (!isAdmin) {
    return <div className="kiosk-admin-page"><p>BHI assessment kiosk settings are admin-only.</p></div>;
  }

  return (
    <div className="kiosk-admin-page">
      <h2>BHI Assessment Kiosk</h2>

      {/* ── Settings ─────────────────────────────────────────────────── */}
      <section className="kiosk-card">
        <h3>Settings</h3>
        {settingsError && <div className="kiosk-admin-error">Error: {settingsError}</div>}

        <div className="kiosk-field kiosk-toggle-row">
          <label>Enable BHI assessment kiosk</label>
          <input type="checkbox" checked={enabled} disabled={savingSettings}
            onChange={(e) => saveSetting('bhi_assessment_kiosk_enabled', e.target.checked)} />
        </div>

        <div className="kiosk-field">
          <label>Scale mode</label>
          <select value={scaleMode} disabled={savingSettings}
            onChange={(e) => saveSetting('bhi_assessment_kiosk_scale_mode', e.target.value)}>
            <option value="top">Top scale only (highest-priority match per patient)</option>
            <option value="all">All applicable scales</option>
          </select>
          <div className="kiosk-hint">
            "Top" shows one scale per session; "All" shows every matched scale in order.
          </div>
        </div>

        <div className="kiosk-field">
          <label>PIN max attempts before lockout</label>
          <input type="number" value={pinMaxAttempts} min={1} max={20} disabled={savingSettings}
            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && Number.isInteger(v) && v >= 1) saveSetting('bhi_assessment_kiosk_pin_max_attempts', v); }} />
        </div>

        <div className="kiosk-field">
          <label>Lockout duration (minutes)</label>
          <input type="number" value={lockoutMinutes} min={1} max={1440} disabled={savingSettings}
            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && Number.isInteger(v) && v >= 1) saveSetting('bhi_assessment_kiosk_pin_lockout_minutes', v); }} />
        </div>

        <div className="kiosk-field">
          <label>Item 9 escalation message</label>
          <textarea rows={4} value={item9Message} disabled={savingSettings}
            maxLength={2000}
            placeholder="Message shown to the caregiver when a resident answers item 9 > 0…"
            onChange={(e) => setSettings({ ...settings, bhi_assessment_kiosk_item9_message: e.target.value })} />
          <button className="btn sm" disabled={savingSettings} onClick={saveItem9Message}>
            {savingSettings ? 'Saving…' : 'Save message'}
          </button>
          <div className="kiosk-hint">
            Displayed as a safety overlay when a resident indicates any thoughts of self-harm (PHQ-9 item 9, etc.).
          </div>
        </div>
      </section>

      {/* ── Facilities ───────────────────────────────────────────────── */}
      <section className="kiosk-card">
        <h3>Facilities</h3>
        {facilitiesError && <div className="kiosk-admin-error">Error: {facilitiesError}</div>}

        <div className="kiosk-add-device">
          <input type="text" placeholder="New facility name" value={newFacilityName}
            onChange={(e) => setNewFacilityName(e.target.value)} maxLength={200} />
          <button className="btn sm" disabled={creatingFacility} onClick={onCreateFacility}>
            {creatingFacility ? 'Creating…' : 'Create facility'}
          </button>
        </div>

        {facilities.length === 0 && (
          <p className="kiosk-hint">No facilities yet. Create one above to get started.</p>
        )}

        <div className="kiosk-facility-list">
          {facilities.map((f) => (
            <div key={f.id}
              className={`kiosk-facility-row${activeFacility?.id === f.id ? ' kiosk-facility-active' : ''}${f.archived_at ? ' kiosk-revoked' : ''}`}>
              <button className="btn ghost sm" onClick={() => setActiveFacility(activeFacility?.id === f.id ? null : f)}>
                {activeFacility?.id === f.id ? '▾' : '▸'} {f.name}
                {f.archived_at && <em> (archived)</em>}
              </button>
              {!f.archived_at && (
                <button className="btn ghost sm kiosk-danger" onClick={() => onArchiveFacility(f)}>
                  Archive
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Per-facility panel ───────────────────────────────────────── */}
      {activeFacility && (
        <section className="kiosk-card">
          <h3>{activeFacility.name}</h3>
          <FacilityElationTags facility={activeFacility} onSaved={onFacilitySaved} />
          <FacilityResidents facilityId={activeFacility.id} patients={patients} />
          <FacilityCaregivers facilityId={activeFacility.id} />
          <FacilityDevices facilityId={activeFacility.id} />
        </section>
      )}
    </div>
  );
}
